import './style.scss';

import { useMemo, useRef, useState } from 'react';
import { useForm, useFormState } from 'react-hook-form';
import { useTranslation } from 'react-i18next';
import { useMutation, useQuery } from 'react-query';
import { useLocation, useNavigate, useParams, useSearchParams } from 'react-router-dom';
import ReactRouterPrompt from 'react-router-prompt';

import { yupResolver } from '@hookform/resolvers/yup';
import { Button } from 'primereact/button';
import { Dialog } from 'primereact/dialog';
import { Menu } from 'primereact/menu';
import {
  create as createFinancialReview,
  downloadDocxTemplate,
  downloadPdfTemplate,
  getDetail,
  getInfo,
} from 'apis/financialReviews.api';

import ErrorDialog from 'components/ErrorDialog';
import Loading from 'components/Loading';
import { TOAST } from 'constant';
import { toast } from 'layout';

import { DetailInfo, EvaluationForm } from 'features/FinancialReview/components';
import { createFinancialReviewValidationSchema } from 'features/FinancialReview/validation';

import { is1593, isMemberAllowedReview } from 'utils/func';
import { validateGeneralInfoWithPayment } from 'utils/validation/generalInfo';
import { getAllVersions } from 'apis/projectProposals.api';
import Signature from 'features/PrequalificationReview/components/Signature';
import DynamicFinancialForm from 'features/FinancialReview/components/DynamicFinanceForm';

export default function FinancialReviewFormCreate() {
  // #region Data
  const { t } = useTranslation();
  const [isVisibleErrorDialog, setIsVisibleDialogError] = useState(false);

  const navigate = useNavigate();
  const location = useLocation();
  const { fromInternal } = location.state || { fromInternal: false };
  const { projectProposalId, councilId } = useParams();
  const [searchParams] = useSearchParams();
  const councilMemberId = searchParams.get('councilMemberId');
  const userId = searchParams.get('userId');
  const downloadTemplateButtonRef = useRef();

  const {
    handleSubmit,
    control,
    setValue,
    reset,
    formState: { errors },
  } = useForm({ mode: 'onChange', resolver: yupResolver(createFinancialReviewValidationSchema) });

  const { isDirty, dirtyFields } = useFormState({ control });

  const {
    mutate,
    error: errorRes,
    isError: isErrorRes,
    isLoading: isLoadingCreate,
  } = useMutation(createFinancialReview);

  // A. General Information
  const { data: allProjectProposalVersionsData } = useQuery(
    ['allProjectProposalVersions', projectProposalId],
    () => getAllVersions(projectProposalId)
  );

  // The projectProposal version ONLY used to get information of projectProposal (A. General)
  const specializationVersion = useMemo(() => {
    if (allProjectProposalVersionsData?.data?.length > 0) {
      return allProjectProposalVersionsData?.data?.find((item) => item?.version === 3) ?? null;
    }
    return null;
  }, [allProjectProposalVersionsData]);
  const { data: infoData, refetch: refetchReviewerInfo } = useQuery(
    ['financial-review', 'project-proposal', councilId, specializationVersion?.id, userId],
    ({ queryKey: [, , _councilId, _projectProposalId, _userId] }) =>
      getInfo(_councilId, _projectProposalId, _userId),
    { enabled: !!specializationVersion?.id }
  );
  const info = useMemo(() => infoData?.data, [infoData]);

  const { data: financialReviewData, isLoading: isLoadingFinancialReview } = useQuery(
    ['financial-review', 'detail', councilMemberId, councilId, projectProposalId],
    ({ queryKey: [, , _councilMemberId, _councilId, _projectProposalId] }) =>
      getDetail(_councilMemberId, _councilId, _projectProposalId),
    {
      enabled: !fromInternal,
    }
  );
  // #endregion

  // #region Events
  const onSubmit = async (data) => {
    // Kiểm tra thông tin chung (bao gồm cả payment info)
    const generalInfoError = validateGeneralInfoWithPayment(info?.projectProposal, info?.user, t);
    if (generalInfoError) {
      toast(TOAST.ERROR, generalInfoError);
      return;
    }

    // Kiểm tra chữ ký trước khi save
    if (!info?.user?.signatureUrl) {
      toast(TOAST.ERROR, t('council.review.signatureRequired'));
      return;
    }

    // B1
    const specializeRequestReview = data?.specializeRequestReview;

    // B3
    const financialReviewResearchKpis = data?.financialReviewResearchKpis;

    // B4
    const expenditureAndKpiComparison = data?.expenditureAndKpiComparison || 0;
    const totalSuggestionExpenditureFromVNUHCMOfReviewer =
      data?.totalSuggestionExpenditureFromVNUHCMOfReviewer || 0;

    const reviewTotalExpenditureFromVNUHCM = JSON.stringify({
      expenditureAndKpiComparison,
      totalSuggestionExpenditureFromVNUHCMOfReviewer,
    });

    // B5
    const inappropriateExpenses = data?.inappropriateExpenses;

    // C
    const totalSuggestionExpenditureFromVNUHCM = data?.totalSuggestionExpenditureFromVNUHCM || 0;
    const totalSuggestionExpenditureFromMobilizedCapital =
      data?.totalSuggestionExpenditureFromMobilizedCapital || 0;
    const ownerEdittingRequest = data?.ownerEdittingRequest;

    const submitData = {
      projectProposalId,
      councilId,
      councilMemberId,
      specializeRequestReview,
      financialReviewResearchKpis,
      reviewTotalExpenditureFromVNUHCM,
      inappropriateExpenses,
      totalSuggestionExpenditureFromVNUHCM,
      totalSuggestionExpenditureFromMobilizedCapital,
      ownerEdittingRequest,
    };

    mutate(submitData, {
      onSuccess: () => {
        reset({}, { keepValues: true });
        toast(TOAST.SUCCESS, t('council.review.createSuccess'));
        setTimeout(() => {
          const queryParams = `councilMemberId=${councilMemberId}&userId=${userId}`;
          navigate(
            `/projects-revise/${projectProposalId}/council/${councilId}/financial-review/edit?${queryParams}`,
            { replace: true }
          );
        }, 500);
      },
      onError: () => {
        setIsVisibleDialogError(true);
      },
    });
  };

  const {
    refetch: handleDownloadPdfTemplate,
    isLoading: isDownloadPdfTemplateLoading,
    isFetching: isDownloadPdfTemplateFetching,
  } = useQuery(
    ['downloadPdfTemplateFinancialReview', specializationVersion?.id, councilId, councilMemberId],
    () =>
      downloadPdfTemplate({
        projectProposalId: specializationVersion?.id,
        councilId,
        councilMemberId,
      }),
    {
      enabled: false,
      onSuccess: (res) => {
        if (res?.data) {
          const file = new Blob([res.data]);

          const filename = 'Mau M02 - Phieu nhan xet danh gia tham dinh kinh phi.pdf';

          const link = document.createElement('a');
          link.href = URL.createObjectURL(file);
          link.download = filename;
          link.click();
        }
      },
      onError: () => {
        toast(TOAST.ERROR, t('projectProposal.review.exportPdfErrorTitle'));
      },
    }
  );

  const {
    refetch: handleDownloadDocxTemplate,
    isLoading: isDownloadDocxTemplateLoading,
    isFetching: isDownloadDocxTemplateFetching,
  } = useQuery(
    ['downloadDocxTemplateFinancialReview', specializationVersion?.id, councilId, councilMemberId],
    () =>
      downloadDocxTemplate({
        projectProposalId: specializationVersion?.id,
        councilId,
        councilMemberId,
      }),
    {
      enabled: false,
      onSuccess: (res) => {
        if (res?.data) {
          const file = new Blob([res.data]);

          const filename = 'Mau M02 - Phieu nhan xet danh gia tham dinh kinh phi.docx';

          const link = document.createElement('a');
          link.href = URL.createObjectURL(file);
          link.download = filename;
          link.click();
        }
      },
      onError: () => {
        toast(TOAST.ERROR, t('projectProposal.review.exportDocxErrorTitle'));
      },
    }
  );

  const footerConfirmationPrompt = (onConfirm, onCancel) => (
    <div>
      <Button
        label={t('formLayout.action.cancel')}
        className="w-7rem"
        severity="danger"
        onClick={onCancel}
        autoFocus
      />
      <Button
        label={t('formLayout.action.ok')}
        className="w-7rem"
        severity="info"
        onClick={onConfirm}
      />
    </div>
  );
  // #endregion

  const template = useMemo(() => info?.projectProposal?.template, [info]);

  const renderFirstTab = () => {
    if (is1593(template)) {
      return (
        <DynamicFinancialForm
          projectProposalId={projectProposalId}
          councilId={councilId}
          councilMemberId={councilMemberId}
          generalInfo={info?.projectProposal}
          isLoading={isLoadingCreate}
        />
      );
    }
    return (
      <div className="m-0 bg-white px-4 sm:px-4 md:px-4 lg:px-6 xl:px-8 mb-3 py-5 p-fluid relative financial-review-create">
        <div className="flex gap-1 justify-content-end">
          <Button
            label={t('council.review.downloadTemplate')}
            icon={`pi ${
              isDownloadPdfTemplateLoading ||
              isDownloadPdfTemplateFetching ||
              isDownloadDocxTemplateLoading ||
              isDownloadDocxTemplateFetching
                ? 'pi-spin pi-spinner'
                : 'pi-file'
            }`}
            className="block w-auto h-3rem border-none"
            severity="info"
            onClick={(event) => downloadTemplateButtonRef.current.toggle(event)}
            disabled={
              isDownloadPdfTemplateLoading ||
              isDownloadPdfTemplateFetching ||
              isDownloadDocxTemplateLoading ||
              isDownloadDocxTemplateFetching
            }
          />
        </div>
        <Menu
          ref={downloadTemplateButtonRef}
          model={[
            {
              label: t('council.review.downloadPdfTemplate'),
              icon: 'pi pi-file-pdf',
              command: handleDownloadPdfTemplate,
              disabled: isDownloadPdfTemplateLoading || isDownloadPdfTemplateFetching,
            },
            {
              label: t('council.review.downloadDocxTemplate'),
              icon: 'pi pi-file-word',
              command: handleDownloadDocxTemplate,
              disabled: isDownloadDocxTemplateLoading || isDownloadDocxTemplateFetching,
            },
          ]}
          popup
        />

        <div className="text-center my-6">
          <h3 className="font-bold mb-0">{t('council.m02.title')}</h3>
          <h3 className="mt-0">{t('council.m02.sciTechProject')}</h3>
        </div>

        <DetailInfo info={info} />

        <form autoComplete="off" onSubmit={handleSubmit(onSubmit)}>
          <EvaluationForm
            setValue={setValue}
            control={control}
            errors={errors}
            proposedExpenditureSummaries={info?.projectProposal?.proposedExpenditureSummaries}
            projectProposalResearchKpis={info?.projectProposal?.projectProposalResearchKpis}
            submittedAt={info?.projectProposal?.submittedAt}
            expenditureCalculatedByKpi={info?.projectProposal?.proposedExpenditureByKpis}
          />

          <div className="grid text-lg mt-3">
            <div className="col-7 px-8">
              <span>{t('council.m01.c.commitment')}</span>
            </div>
            <div className="col-5 text-center">
              <Signature refetchReviewerInfo={refetchReviewerInfo} reviewerInfo={info?.user} />
            </div>
          </div>

          <div className="flex justify-content-end mt-4">
            <Button
              label={t('formLayout.action.close')}
              severity="danger"
              type="button"
              className="mr-2 w-8rem"
              onClick={() => navigate(-1)}
            />
            <Button
              label={t('formLayout.action.save')}
              severity="info"
              type="submit"
              className="w-8rem"
              onClick={() => {
                if (Object.keys(errors || {}).length) {
                  toast(TOAST.ERROR, t('errorMessage.validationErrorMessage'));
                }
              }}
              disabled={
                !(isDirty && Object.keys(dirtyFields).length) ||
                !isMemberAllowedReview(info?.user?.councilMembers?.[0])
              }
            />
          </div>
        </form>
      </div>
    );
  };

  if (!isLoadingFinancialReview && financialReviewData?.data?.financialReview) {
    const queryParams = `councilMemberId=${councilMemberId}&userId=${userId}`;
    navigate(
      `/projects-revise/${projectProposalId}/council/${councilId}/financial-review/edit?${queryParams}`,
      { replace: true }
    );
  }

  return (
    <>
      {isLoadingCreate && <Loading />}

      {isErrorRes && (
        <ErrorDialog
          title={t('council.review.create')}
          isError={isErrorRes}
          error={errorRes}
          visible={isVisibleErrorDialog}
          setVisible={setIsVisibleDialogError}
        />
      )}
      <ReactRouterPrompt when={isDirty && Object.keys(dirtyFields).length}>
        {({ isActive, onConfirm, onCancel }) => (
          <Dialog
            visible={isActive}
            header={<p className="text-blue-500">{t('projectProposal.confirmationTitle')}</p>}
            position="center"
            style={{ width: '300px' }}
            onHide={onCancel}
            draggable={false}
            footer={() => footerConfirmationPrompt(onConfirm, onCancel)}
          >
            <p className="text-lg py-3">{t('projectProposal.confirmationPromptMessage')}</p>
          </Dialog>
        )}
      </ReactRouterPrompt>
      {renderFirstTab()}
    </>
  );
}
